import React from 'react'

interface DashboardGridIconProps {
  className?: string
  size?: number
  variant?: 'default' | 'active' | 'hover'
}

export function DashIcon({
  className = '',
  size,
  variant = 'default'
}: DashboardGridIconProps) {
  const colors = {
    default: {
      primary: '#94a3b8', // slate-400
      secondary: '#64748b', // slate-500
      accent: '#fb2ba9', // polari pink
      glow: 'rgba(251, 43, 169, 0.2)'
    },
    active: {
      primary: '#fb2ba9',
      secondary: '#e91e8c',
      accent: '#fb2ba9',
      glow: 'rgba(251, 43, 169, 0.4)'
    },
    hover: {
      primary: '#cbd5e1', // slate-300
      secondary: '#94a3b8', // slate-400
      accent: '#fb2ba9',
      glow: 'rgba(251, 43, 169, 0.3)'
    }
  }

  const currentColors = colors[variant]

  return (
    <>
      <style>{`
        @keyframes pulse-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes color-blink-top {
          0%, 100% { fill: ${currentColors.secondary}; }
          15%, 30% { fill: #CC339F; }
        }
        
        @keyframes color-blink-bottom-left {
          0%, 100% { fill: ${currentColors.secondary}; }
          35%, 50% { fill: #6600FF; }
        }
        
        @keyframes color-blink-bottom-right {
          0% { fill: ${currentColors.primary}; }
          55%, 100% { fill: #00FFC0; }
        }
        
        .dashboard-grid-hover .card-top {
          animation: pulse-scale 0.6s ease-in-out, color-blink-top 1.2s ease-in-out;
          transform-origin: center;
        }
        
        .dashboard-grid-hover .card-bottom-left {
          animation: pulse-scale 0.6s ease-in-out 0.1s, color-blink-bottom-left 1.2s ease-in-out;
          transform-origin: center;
        }
        
        .dashboard-grid-hover .card-bottom-right {
          animation: pulse-scale 0.6s ease-in-out 0.2s, color-blink-bottom-right 1.2s ease-in-out forwards;
          transform-origin: center;
        }
      `}</style>

      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${className} ${variant === 'hover' ? 'dashboard-grid-hover' : ''}`}
      >
        {/* Glow effect */}
        {variant === 'active' && (
          <circle
            cx="12"
            cy="12"
            r="10"
            fill={currentColors.glow}
            opacity="0.6"
          />
        )}

        {/* Top card (wide) - Blinks #CC339F first */}
        <rect
          className="card-top"
          x="4"
          y="4"
          width="16"
          height="7"
          rx="2"
          fill={currentColors.secondary}
          stroke={variant === 'active' ? currentColors.accent : 'transparent'}
          strokeWidth="1.5"
          opacity="0.9"
        />

        {/* Bottom left card - Blinks #6600FF second */}
        <rect
          className="card-bottom-left"
          x="4"
          y="13"
          width="7"
          height="7"
          rx="2"
          fill={currentColors.secondary}
          stroke={variant === 'active' ? currentColors.accent : 'transparent'}
          strokeWidth="1.5"
          opacity="0.9"
        />

        {/* Bottom right card - Holds #00FFC0 last */}
        <rect
          className="card-bottom-right"
          x="13"
          y="13"
          width="7"
          height="7"
          rx="2"
          fill={currentColors.primary}
          stroke={variant === 'active' ? currentColors.accent : 'transparent'}
          strokeWidth="1.5"
        />

        {/* Content indicators - small dots/lines on cards */}
        <circle cx="7.5" cy="7.5" r="1" fill="currentColor" opacity="0.4" />
        <circle cx="7.5" cy="16.5" r="1" fill="currentColor" opacity="0.4" />
        <circle cx="16.5" cy="16.5" r="1" fill="currentColor" opacity="0.4" />

        {/* Accent dot for active state */}
        {variant === 'active' && (
          <circle
            cx="19"
            cy="6"
            r="2"
            fill={currentColors.accent}
          />
        )}
      </svg>
    </>
  )
}
